import React, { useEffect, useState, useCallback } from 'react';
import './AdminTrips.css';

// Admin panel revamped to use JWT (username/password) instead of static key.

function AdminTrips() {
  console.log('[AdminTrips] component mount');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [token, setToken] = useState(() => sessionStorage.getItem('adminToken') || '');
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [noteDraft, setNoteDraft] = useState('');
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [search, setSearch] = useState('');
  const [statusInfo, setStatusInfo] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [stats, setStats] = useState([]);

  // The API lives on Render, not on the Vercel origin that serves this app.
  const apiBase = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' ? 'http://localhost:5001' : 'https://safarnamah-backend-lc7p.onrender.com');

  const fetchStatus = useCallback(async () => {
    try {
      const resp = await fetch(`${apiBase}/api/admin/status`);
      const data = await resp.json();
      setStatusInfo(data);
    } catch {}
  }, [apiBase]);

  async function login(e) {
    e && e.preventDefault();
    setError(null);
    try {
      const resp = await fetch(`${apiBase}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json' },
        body: JSON.stringify({ username, password })
      });
      const contentType = resp.headers.get('content-type') || '';
      if (!contentType.includes('application/json')) {
        throw new Error('Unexpected response (HTML) – check API base URL.');
      }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Login failed');
      setToken(data.token);
      sessionStorage.setItem('adminToken', data.token);
      setPassword('');
      setPage(1);
      fetchTrips(1);
      fetchStats();
    } catch (e) { setError(e.message); }
  }

  const fetchTrips = useCallback(async (targetPage = page) => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', targetPage);
      params.set('pageSize', pageSize);
      if (search.trim()) params.set('search', search.trim());
      if (filter === 'responded') params.set('responded','true');
      if (filter === 'pending') params.set('responded','false');
      const resp = await fetch(`${apiBase}/api/admin/trips?${params.toString()}`, { headers: { Authorization: `Bearer ${token}` }});
      const ct = resp.headers.get('content-type') || '';
      if (!ct.includes('application/json')) {
        throw new Error('Unexpected non-JSON response; API base misconfigured.');
      }
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Failed to fetch trips');
      setTrips(data.items);
      setTotalPages(data.totalPages);
      setPage(data.page);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [apiBase, token, page, pageSize, search, filter]);

  useEffect(()=>{ fetchStatus(); },[fetchStatus]);

  function filteredTrips(){
    let list = trips;
    if (filter === 'responded') list = list.filter(t=>t.responded);
    if (filter === 'pending') list = list.filter(t=>!t.responded);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(t => (
        (t.name||'').toLowerCase().includes(q) ||
        (t.destination||'').toLowerCase().includes(q) ||
        (t.email||'').toLowerCase().includes(q) ||
        (t.id||'').toLowerCase().includes(q)
      ));
    }
    return list;
  }

  async function saveUpdate(trip, respondedFlag) {
    if (!token) return;
    try {
      const resp = await fetch(`${apiBase}/api/admin/trips/${trip.id}`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ responded: respondedFlag, internalNotes: noteDraft })
      });
      if (!resp.ok) throw new Error('Update failed');
      const { trip: updated } = await resp.json();
      setTrips(prev => prev.map(t => t.id === updated.id ? updated : t));
      setSelectedTrip(null); setNoteDraft('');
    } catch(e){ alert(e.message); }
  }

  async function deleteTrip(tripId) {
    if (!token) return;
    if (!window.confirm('Are you sure you want to delete this trip request?')) return;
    try {
      const resp = await fetch(`${apiBase}/api/admin/trips/${tripId}`, {
        method: 'DELETE',
        headers: { 'Content-Type':'application/json', Authorization: `Bearer ${token}` }
      });
      if (!resp.ok) throw new Error('Delete failed');
      setTrips(prev => prev.filter(t => t.id !== tripId));
      setSelectedTrip(null);
      alert('Trip request deleted successfully');
    } catch(e){ alert('Delete error: ' + e.message); }
  }

  function exportCSV() {
    if (!token) return;
    fetch(`${apiBase}/api/admin/trips/export`, { headers: { Authorization: `Bearer ${token}` }})
      .then(r=> { if(!r.ok) throw new Error('Export failed'); return r.text(); })
      .then(text => {
        const blob = new Blob([text], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = 'trip-requests.csv'; a.click();
        URL.revokeObjectURL(url);
      }).catch(e => alert(e.message));
  }

  async function exportXLSX() {
    if (!trips.length || !token) return;
    try {
      const XLSX = await import('xlsx');
      const sheetData = trips.map(t => ({
        ID: t.id,
        Name: t.name,
        Email: t.email,
        Phone: t.phone || '',
        Destination: t.destination,
        StartDate: t.startDate || '',
        EndDate: t.endDate || '',
        Travelers: t.travelers || '',
        Budget: t.budget || '',
        Responded: t.responded ? 'Yes' : 'No',
        Interests: Array.isArray(t.interests) ? t.interests.join(', ') : '',
        SpecialRequirements: (t.specialRequirements || '').replace(/<br>/g,' '),
        InternalNotes: t.internalNotes || ''
      }));
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(sheetData);
      XLSX.utils.book_append_sheet(wb, ws, 'Trips');
      XLSX.writeFile(wb, 'trip-requests.xlsx');
    } catch (e) {
      alert('XLSX export failed: ' + e.message);
    }
  }

  // counts derived from statusInfo now; remove local responded/pending counts

  const fetchStats = useCallback(async () => {
    if (!token) return;
    try {
      const resp = await fetch(`${apiBase}/api/admin/stats/daily?days=30`, { headers: { Authorization: `Bearer ${token}` }});
      const data = await resp.json();
      if (resp.ok) setStats(data.series);
    } catch {}
  }, [apiBase, token]);

  useEffect(()=>{ if (token) fetchTrips(page); },[filter, search, pageSize, token, fetchTrips, page]);
  useEffect(()=>{ if (token) fetchStats(); },[token, fetchStats]);

  if (!token) {
    return (
      <div className="admin-trips-container">
        <h1>Admin Login</h1>
        <form onSubmit={login} className="admin-login-form">
          <input placeholder="Username" value={username} onChange={e=>setUsername(e.target.value)} />
          <input type="password" placeholder="Password" value={password} onChange={e=>setPassword(e.target.value)} />
          <button type="submit">Login</button>
        </form>
        {error && <div className="error">{error}</div>}
      </div>
    );
  }

  return (
    <div className="admin-trips-container">
      <h1>Admin Trip Requests</h1>
      <div className="admin-status-bar">
        {statusInfo && (
          <>
            <span>Total: {statusInfo.counts.total}</span>
            <span>Pending: {statusInfo.counts.pending}</span>
            <span>Responded: {statusInfo.counts.responded}</span>
            <span>Page: {page}/{totalPages}</span>
          </>
        )}
      </div>
      <div className="admin-controls">
        <button onClick={()=>fetchTrips(page)} disabled={loading}>Refresh</button>
        <select value={filter} onChange={e=>setFilter(e.target.value)}>
          <option value="all">All</option>
          <option value="pending">Pending</option>
          <option value="responded">Responded</option>
        </select>
        <input
          type="text"
          placeholder="Search name/destination/email/id"
          value={search}
          onChange={e=>{ setSearch(e.target.value); setPage(1); }}
          style={{flex:'1 1 300px'}}
        />
        <select value={pageSize} onChange={e=>{ setPageSize(parseInt(e.target.value)); setPage(1); }}>
          {[10,20,50,100].map(n => <option key={n} value={n}>{n}/page</option>)}
        </select>
        <button onClick={exportCSV} disabled={!trips.length}>Export CSV</button>
        <button onClick={exportXLSX} disabled={!trips.length}>Export XLSX</button>
        <button onClick={()=>{ sessionStorage.removeItem('adminToken'); setToken(''); }}>Logout</button>
      </div>
      {error && <div className="error">{error}</div>}
      {loading && <div className="loading">Loading...</div>}
      <table className="trips-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Name</th>
            <th>Destination</th>
            <th>Travelers</th>
            <th>Budget</th>
            <th>Responded</th>
            <th>Notes</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredTrips().map(trip => (
            <tr key={trip.id} className={trip.responded ? 'responded' : 'pending'}>
              <td>{trip.name}</td>
              <td>{trip.destination}</td>
              <td>{trip.travelers || '—'}</td>
              <td>{trip.budget || '—'}</td>
              <td>{trip.responded ? 'Yes' : 'No'}</td>
              <td className="notes-cell">{(trip.internalNotes||'').slice(0,40)}{(trip.internalNotes||'').length>40?'…':''}</td>
              <td className="row-actions">
                <button title="View / Edit" onClick={()=>{ setSelectedTrip(trip); setNoteDraft(trip.internalNotes || ''); }}>✎</button>
                <button title="Toggle Responded" onClick={()=>saveUpdate(trip, !trip.responded)}>{trip.responded ? '↺' : '✓'}</button>
              </td>
            </tr>
          ))}
          {!filteredTrips().length && !loading && <tr><td colSpan="7">No trips</td></tr>}
        </tbody>
      </table>
      <div className="pagination-controls">
        <button disabled={page<=1} onClick={()=>setPage(p=>p-1)}>Prev</button>
        <button disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}>Next</button>
      </div>
      <div className="metrics-chart">
        <h2>Last 30 Days</h2>
        <div className="chart-bars">
          {stats.map(d => {
            const max = Math.max(...stats.map(s=>s.count),1);
            const height = (d.count / max) * 100;
            return (
              <div key={d.day} className="bar" title={`${d.day}: ${d.count}`}>
                <div className="bar-fill" style={{height: height+'%'}}></div>
                <span className="bar-label">{d.day.slice(5)}</span>
              </div>
            );
          })}
        </div>
      </div>
      {selectedTrip && (
        <div className="modal">
          <div className="modal-content">
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', paddingBottom: '15px', borderBottom: '2px solid #f0f0f0'}}>
              <div>
                <h2 style={{margin: '0 0 10px 0'}}>Trip #{selectedTrip.id}</h2>
              </div>
              <div style={{textAlign: 'right', fontSize: '0.9rem', color: '#666'}}>
                <p style={{margin: '0 0 5px 0'}}><strong>{selectedTrip.name}</strong></p>
                <p style={{margin: '0 0 3px 0'}}>{selectedTrip.email}</p>
                <p style={{margin: '0 0 8px 0'}}>{selectedTrip.phone || 'No phone'}</p>
              </div>
            </div>

            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px'}}>
              <div>
                <p style={{marginBottom: '12px'}}><strong>Destination:</strong> {selectedTrip.destination}</p>
                <p style={{marginBottom: '12px'}}><strong>Start Date:</strong> {selectedTrip.startDate || 'N/A'}</p>
                <p style={{marginBottom: '12px'}}><strong>Budget:</strong> {selectedTrip.budget || '—'}</p>
                <p style={{marginBottom: '12px'}}><strong>Accommodation:</strong> {selectedTrip.accommodation || '—'}</p>
              </div>
              <div>
                <p style={{marginBottom: '12px'}}><strong>End Date:</strong> {selectedTrip.endDate || 'N/A'}</p>
                <p style={{marginBottom: '12px'}}><strong>Travelers:</strong> {selectedTrip.travelers || '—'}</p>
                <p style={{marginBottom: '12px'}}><strong>Travel With:</strong> {selectedTrip.travelWith || '—'}</p>
                <p style={{marginBottom: '12px'}}><strong>Interests:</strong> {Array.isArray(selectedTrip.interests) ? selectedTrip.interests.join(', ') : '—'}</p>
              </div>
            </div>

            <div style={{marginBottom: '20px', paddingTop: '15px', borderTop: '2px solid #f0f0f0'}}>
              <p style={{marginBottom: '8px'}}><strong>Special Requirements:</strong></p>
              <div style={{backgroundColor: '#f9f9f9', padding: '12px', borderRadius: '4px', maxHeight: '120px', overflowY: 'auto', fontSize: '0.9rem'}}>
                <span dangerouslySetInnerHTML={{ __html: selectedTrip.specialRequirements || '—' }} />
              </div>
            </div>

            <label style={{display: 'block', marginBottom: '15px', marginTop: '20px'}}>
              <strong>Internal Notes</strong>
              <textarea value={noteDraft} onChange={e=>setNoteDraft(e.target.value)} rows={3} style={{width: '100%', marginTop: '8px', padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontFamily: 'inherit'}}></textarea>
            </label>
            <div className="modal-actions">
              <button onClick={()=>saveUpdate(selectedTrip, true)}>Mark Responded</button>
              <button onClick={()=>saveUpdate(selectedTrip, false)}>Mark Pending</button>
              <button onClick={()=>deleteTrip(selectedTrip.id)} style={{backgroundColor: '#dc3545'}}>Delete</button>
              <button onClick={()=>setSelectedTrip(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminTrips;
